import React from "react";
import {Button, DropdownButton, FormControl, FormGroup, InputGroup, MenuItem} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {connect} from "react-redux";
import {Link} from "react-router-dom";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {DefaultReorderControl} from "../common/ui/reorder/DefaultReorderControl";
import {InjectedReorderableListProps, ReorderableList} from "../common/ui/reorder/ReorderableList";
import {ColumnFactory, ColumnWidth, ControlRoomColumn} from "../common/ui/table/ControlRoomColumns";
import {
  CONTROLROOM_TABLE_HEADER_HEIGHT,
  ControlRoomTable,
  ControlRoomTableProperties,
} from "../common/ui/table/ControlRoomTable";
import {ValidationResult, ValidationState} from "../common/ui/validation/ProductValidation";
import {moveItem, moveItems} from "../common/util/ArrayUtil";
import {getConcatenatedValidationMessages, invalidateWMSCache} from "../common/util/Util";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {ImportedDataTypeahead} from "../data/DataTypeahead";
import {ImportedData} from "../data/model";
import {Identifiable} from "../model";
import {SetStyleButton} from "../styles/SetStyleButton";
import {actions} from "./actions";
import {StyledData} from "./model";
import {selectors} from "./selectors";
import {MenuItemSetStyleSetName} from "../styles/MenuItemSetStyleSetName";
import Alert from "react-bootstrap/lib/Alert";

interface DataSetStyleEntry {
  dataId: string;
  dataTitle: string;
  style: string;
  styleTitle: string;
}

interface StyleSet {
  abstractText: string;
  name: string;  // Name for the styleSet, must be unique
  title: string;
  id: string;
  styles: DataSetStyleEntry[];
}

type StyleSetArray = StyleSet[]

const DefaultStyleSetTitlePrefix = "Style set ";

interface ProductContentListComponentState {
  dataToAdd: ImportedData;
  styledDataToAdd: StyledData;
  selectedIndices: number[];
  isBulkRemovingEntries: boolean;
  validation: ValidationResult;
  currentStyleSetId: string;
  defaultStyleSetId: string;
  availableStyleSets: StyleSetArray;
}

type ColumnEntry = ControlRoomColumn<StyledData>;

class ProductContentColumnFactory implements ColumnFactory<StyledData> {

  _removeItem: (item: StyledData, index: number) => void;
  _moveSelection: (toIdx: number) => void;
  _changeItem: (changedItem: StyledData, index: number) => void;
  _withVisibilityColumn: boolean;

  columns: { [columnID: string]: ColumnEntry } = {
    data: {
      header: <FormattedMessage id="studio.products.product-content-list.data" defaultMessage="Data"/>,
      cellContent: (styledData) => {
        return <Link to={`/data/${encodeURIComponent(styledData.data)}`}>{this.getDataTitle(styledData)}</Link>;
      },
      cellTooltip: (styledData) => this.getDataTitle(styledData),
      columnWidth: ColumnWidth.flex(2),
    },
    style: {
      header: <FormattedMessage id="studio.products.product-content-list.style" defaultMessage="Style"/>,
      cellContent: (styledData, index, selected) => {
        const style = styledData.style;
        if (style) {
          return (<Link to={`/styles/${styledData.style}`}>{styledData.styleTitle}</Link>);
        }
        return <FormattedMessage id="studio.products.product-content-list.no-style" defaultMessage="No style set"/>;
      },
      cellTooltip: (styledData, intl) => styledData.style ? styledData.styleTitle : intl.formatMessage(
          PRODUCT_CONTENT_MESSAGES.noStyle),
      columnWidth: ColumnWidth.flex(2),
    },
    move: {
      header: <FormattedMessage id="studio.products.product-content-list.move" defaultMessage="Move"/>,
      cellContent: (styledData, index, selected, allData, selectedIndices) => {
        if (selected && selectedIndices.length === 1) {
          invalidateWMSCache();
          return (
              <DefaultReorderControl
                  moveSelection={this._moveSelection}
                  selectedIndex={index}
                  maxIndex={allData.length - 1}
              />
          );
        }
        return null;
      },
      cellTooltip: (styledData, intl) => intl.formatMessage(PRODUCT_CONTENT_MESSAGES.moveTooltip),
      columnWidth: ColumnWidth.pixels(70),
    },
    visible: {
      header: <FormattedMessage id="studio.products.product-content-list.visible" defaultMessage="Visible"/>,
      cellContent: (styledData, index) => {
        return (<Button className="btn-icon" style={{backgroundColor: "transparent", color: "currentColor"}}
                        onClick={(event) => {
                          const changedStyledData = {...styledData, visible: !styledData.visible};
                          this._changeItem(changedStyledData, index);
                          invalidateWMSCache();
                          event.stopPropagation(); //avoid that ControlRoomTable's onRowClick gets called
                        }}><LcdIcon icon={styledData.visible ? "eye" : "eye-close"}/></Button>);
      },
      cellTooltip: (styledData, intl) => intl.formatMessage(PRODUCT_CONTENT_MESSAGES.visibleTooltip),
      columnWidth: ColumnWidth.pixels(65),
    },
  };

  constructor(removeItem: ((item: StyledData, index: number) => void),
              moveSelection: (toIdx: number) => void,
              changeItem: (changedItem: StyledData, index: number) => void,
              withVisibilityColumn: boolean) {
    this._removeItem = removeItem;
    this._moveSelection = moveSelection;
    this._changeItem = changeItem;
    this._withVisibilityColumn = withVisibilityColumn;
  }

  getDataTitle(styledData: StyledData) {
    return (styledData.dataTitle) ? styledData.dataTitle : "UNKNOWN_DATA_TITLE";
  }

  getInitialColumnIDs(): string[] {
    return this.getAvailableColumnIDs();
  }

  getInitialColumnWidth(columnID: string): ColumnWidth {
    return this.columns[columnID].columnWidth || ColumnWidth.pixels(200);
  }

  getAvailableColumnIDs(): string[] {
    const availableColumnIDs = [];
    for (const prop in this.columns) {
      if (this.columns.hasOwnProperty(prop) && !(prop === "visible" && !(this._withVisibilityColumn))) {
        availableColumnIDs.push(prop);
      }
    }
    return availableColumnIDs;
  }

  createColumn(columnID: string): ControlRoomColumn<StyledData> {
    return this.columns[columnID];
  }

}

interface OwnProductContentListProps {
  productId?: string;
  disableStyleEditing?: boolean;
  wmsStyle?: string;
  onSetWmsStyle?: (s: string) => void;
  initialLoad?: () => void;
  onBulkChange: (changedItems: StyledData[], changedIndices: number[]) => Promise<void>;
  onBulkRemove: (removeItems: StyledData[], items: StyledData[]) => Promise<void>;
  addDataPlaceHolder?: string;
  withVisibilityToggle: boolean;
  calculateDimensions: (parent: HTMLElement) => {};
}

type OwnProductContentListPropsWithApi = OwnProductContentListProps & WithApiProperties;

interface ProductContentListDispatchProps {
  onReorder?: (oldIndex: number | number[], newIndex: number, items: StyledData[]) => void;
}

type ProductContentListComponentProps =
    InjectedReorderableListProps<StyledData>
    & InjectedIntlProps
    & OwnProductContentListPropsWithApi
    & ProductContentListDispatchProps;

type StyledDataFactoryFunction = (oldStyledData: StyledData, ...args) => StyledData;

const PRODUCT_CONTENT_MESSAGES = defineMessages({
  dataPlaceholder: {
    id: "studio.products.product-content-list.data-placeholder",
    defaultMessage: "Search for data to add",
  },
  toggleVisibilityDescription: {
    id: "studio.products.product-content-list.toggle-visibility-description",
    defaultMessage: "Toggle visibility of selected items",
  },
  selectAllItemsDescription: {
    id: "studio.products.product-content-list.select-all-items-description",
    defaultMessage: "Select all items",
  },
  clearSelectionDescription: {
    id: "studio.products.product-content-list.clear-selection-description",
    defaultMessage: "Clear selection",
  },
  removeSelectedItems: {
    id: "studio.products.product-content-list.remove-selected-items",
    defaultMessage: "Remove selected items",
  },
  noStyle: {
    id: "studio.products.product-content-list.no-style",
    defaultMessage: "No style set",
  },
  moveTooltip: {
    id: "studio.products.product-content-list.move",
    defaultMessage: "Move",
  },
  visibleTooltip: {
    id: "studio.products.product-content-list.visible-tooltip",
    defaultMessage: "Toggle visibility",
  },
  actionTitle: {
    id: "studio.products.product-content-list.action-title",
    defaultMessage: "Action",
  },
  defaultStyle: {
    id: "studio.products.product-content-list.default-style",
    defaultMessage: "Default style set"
  },
  moveSelectionTop: {
    id: "studio.products.product-content-list.move-selection-top",
    defaultMessage: "Move selection to the top",
  },
  moveSelectionBottom: {
    id: "studio.products.product-content-list.move-selection-bottom",
    defaultMessage: "Move selection to the bottom",
  }
});

export class ProductContentListComponent extends React.Component<ProductContentListComponentProps, ProductContentListComponentState> {

  ProductContentListTable: React.ComponentClass<ControlRoomTableProperties<StyledData>>;
  private dragIndex: number | null = null;
  private overIndex: number | null = null;

  constructor(props) {
    super(props);
    this.state = {
      dataToAdd: null,
      styledDataToAdd: null,
      selectedIndices: [],
      isBulkRemovingEntries: false,
      validation: null,
      currentStyleSetId: "",
      defaultStyleSetId: "",
      availableStyleSets: []
    };
    const moveSelection = (toIdx) => {
      props.moveSelection(toIdx);
      this.setState({selectedIndices: [toIdx]});
    };
    this.ProductContentListTable = ControlRoomTable(
        new ProductContentColumnFactory(
            props.removeItem, moveSelection, props.changeItem,
            props.withVisibilityToggle,
        ),
        props.calculateDimensions,
    );
  }

  componentDidMount() {
    if (this.props.initialLoad) {
      this.props.initialLoad();
    }
    this.loadAllStyles(true).then(() => {
      this.loadDefaultStyleSet();
    }).catch((error) => {
      if (error !== "No ProductId") {  //ignore "No ProductId" errors throw everything else back to the console
        throw (error);
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.items.length !== prevProps.items.length) {
      setTimeout(() => {
        this.loadAllStyles();
      }, 1000)
    }
    if (this.props.productId !== prevProps.productId) {
      setTimeout(() => {
        this.loadAllStyles().then(() => {
          this.loadDefaultStyleSet();
        });
      }, 1000)
    }

  }

  onRowDragStart = (e: React.DragEvent<HTMLElement>) => {
    const idx = Number((e.currentTarget as HTMLElement).getAttribute("data-index"));
    this.dragIndex = isNaN(idx) ? null : idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(this.dragIndex ?? ""));
    (e.currentTarget as HTMLElement).classList.add("dragging");

    const selected = this.state.selectedIndices || [];
    const isDraggingSelection = selected.indexOf(idx) !== -1;
    if (!isDraggingSelection) {
      this.setState({selectedIndices: [idx]});
    }
  };

  onRowDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    const idx = Number((e.currentTarget as HTMLElement).getAttribute("data-index"));
    this.overIndex = isNaN(idx) ? this.overIndex : idx;
    e.dataTransfer.dropEffect = "move";
  };

  onRowDragEnd = (e: React.DragEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).classList.remove("dragging");
    this.dragIndex = null;
    this.overIndex = null;
  };

  onRowDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    const from = this.dragIndex ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
    const toAttr = (e.currentTarget as HTMLElement).getAttribute("data-index");
    let to = this.overIndex ?? (toAttr ? Number(toAttr) : null);
    if (from == null || to == null || from === to) {
      this.dragIndex = null;
      this.overIndex = null;
      return;
    }

    if (from < to) { //if dragging selection down list place the target under the dropped item
      ++to;
    }

    const selected = this.state.selectedIndices || [];
    const isDraggingSelection = selected.indexOf(from) !== -1 && selected.length > 1;
    const fromIndices = Array.from(new Set(isDraggingSelection ? selected.slice() : [from])).sort((a, b) => a - b);

    if (this.props.onReorder) {
      const movedItemsSet = new Set(fromIndices.map(i => this.props.items[i]));
      const newContentsLocal = Array.isArray(fromIndices) ? moveItems<StyledData>(this.props.items, fromIndices, to)
                                                          : moveItem<StyledData>(this.props.items, fromIndices, to);
      const selectedIndices = newContentsLocal.reduce((acc: number[], item, idx) => {
        if (movedItemsSet.has(item)) {
          acc.push(idx);
        }
        return acc;
      }, []);
      this.props.onReorder(fromIndices, to, this.props.items);
      this.setState({selectedIndices});
    }

    this.dragIndex = null;
    this.overIndex = null;
  };

  handleDataChange = (newData) => {
    if (newData) {
      this.setState({validation: null});
      const {api} = this.props;

      const styledDataToAdd = {
        id: newData.id,
        productId: this.props.productId,
        data: newData.id,
        dataTitle: newData.title,
        style: newData.defaultStyleId,
        visible: true,
      };

      // Validate only if the product is present
      if (this.props.productId) {
        api.validateStyledData(this.props.productId, [styledDataToAdd]).then(
            (apiValidationResult: ValidationResult) => {
              const hasWarnings = apiValidationResult.errorMessages.length > 0 ||
                                  apiValidationResult.warningMessages.length > 0;

              if (hasWarnings) {
                this.setState({validation: apiValidationResult});
              } else {
                this.setState({dataToAdd: newData, styledDataToAdd}, this.addData);
              }
            });
      } else {
        this.setState({dataToAdd: newData, styledDataToAdd}, this.addData); // data picked and automatically added to backend
      }
    }
  }

  addData = () => {
    const _data = this.state.styledDataToAdd;
    if (_data) {
      this.props.addItem(_data);
      this.setState({dataToAdd: null, styledDataToAdd: null});
    }
  }

  validateDataTypeHead = (data: Identifiable) => {
    const {items, validateItemToAdd} = this.props;
    if (items.some((styledData) => (styledData.data === data.id))) {
      return false;
    }
    if (validateItemToAdd) {
      return validateItemToAdd(data);
    }
    return true;
  }

  _handleBulkStyledDataChange(styledDataFactoryFunction: StyledDataFactoryFunction) {
    const {items} = this.props;
    const changedStyledDatas: StyledData[] = [];
    const changedIndices: number[] = [];
    //save selected indices so the selection can be restored post-update
    for (let i = 0; i < items.length; i++) {
      const styledData = items[i];
      const isSelectedEntry = this.state.selectedIndices.indexOf(i) >= 0;
      if (isSelectedEntry) {
        const newStyledData = styledDataFactoryFunction(styledData);
        changedStyledDatas.push(newStyledData);
        changedIndices.push(i);
      }
    }
    return this.props.onBulkChange(changedStyledDatas, changedIndices);
  }

  updateBulkVisibilityHandler = () => {
    return this._handleBulkStyledDataChange((styledData) => {
      return {...styledData, visible: !styledData.visible};
    });
  }

  private processStyledData = (styleToSet) => (styledData) => {
    const availableStyleSets = [...this.state.availableStyleSets];
    const currentSt = availableStyleSets.find(item => (item.id === this.state.currentStyleSetId));
    if (currentSt) {
      const foundStyle = currentSt.styles.find(x => x.dataId === styledData.id);
      if (foundStyle) {
        foundStyle.style = styleToSet.id;
        foundStyle.styleTitle = styleToSet.title;
        this.setState({availableStyleSets});
      }
    }
    return {
      ...styledData,
      style: styleToSet ? styleToSet.id : null,
      styleTitle: styleToSet ? styleToSet.title : null,
    };
  }

  updateBulkStyleHandler = (styleToSet) => {
    return this._handleBulkStyledDataChange(this.processStyledData(styleToSet));
  }

  forceMapReload = () => {
    invalidateWMSCache();
    return this.props.onBulkChange([], []);
  }

  getSelectedStyledDatas = () => {
    return this.state.selectedIndices.map((index) => this.props.items[index]);
  }

  renderErrors = () => {
    const {validation} = this.state;
    if (validation && validation.errorMessages.length > 0) {
      const errorMessage = getConcatenatedValidationMessages(validation);
      return <Alert bsStyle="danger">{errorMessage}</Alert>;
    }
  }

  private selectStyleSet = (event) => {
    event.preventDefault();
    const {value} = event.target;
    this.setState({
      currentStyleSetId: value
    }, () => {
      this.loadAllStyles().then(() => {
        this.updateWmsStyle();
      });
    })
  }

  private loadDefaultStyleSet = () => {
    return new Promise((resolve, reject) => {
      if (this.props.productId) {
        this.props.api.getAPIProductById(this.props.productId).then((data) => {
          this.setState({
            defaultStyleSetId: data.defaultStyleSet
          }, () => {
            resolve(data.defaultStyleSet)
          })
        }, () => {
          reject()
        });
      } else {
        reject("No ProductId")
      }
    })
  }

  private setDefaultStyleSet = (defaultStyleSet: string) => {
    const data = {
      defaultStyleSet
    }
    this.props.api.setAPIProductById(this.props.productId, data).then((dataReturned) => {
      this.setState({defaultStyleSetId: dataReturned.defaultStyleSet});
    });
  }
  private loadAllStyles = (loadFirst?: boolean) => {
    const loadStyles = (resolve, reject) => {
      this.props.api.getAllProductStyleSetsData(this.props.productId).then(data => {
        if (data.length > 0 && this.state.defaultStyleSetId) {
          const currentStyleSetId = this.state.currentStyleSetId ? this.state.currentStyleSetId
                                                                 : this.state.defaultStyleSetId;
          this.props.api.getProductStyleSetsDataById(this.props.productId, currentStyleSetId).then(content => {
            const cData = this.getAvailableStyleSets(data, currentStyleSetId, content);
            this.setState({
              availableStyleSets: cData,
              currentStyleSetId
            }, () => {
              resolve(true)
            })
          }, () => {
            reject()
          });
        }
      });
    }
    return new Promise((resolve, reject) => {
      this.loadDefaultStyleSet().then(() => {
        loadStyles(resolve, reject);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  private getAvailableStyleSets(data: any[], currentStyleSetId: string, content) {
    const cData = data.map(d => {
      if (currentStyleSetId === d.id) {
        const styles = content.map(c => ({
          dataId: c.data,
          dataTitle: c.dataTitle,
          style: c.style ? c.style : "",
          styleTitle: c.styleTitle ? c.styleTitle : ""
        }))
        return {...d, styles}
      }
      return {...d, styles: []}
    });
    return cData;
  }

  private addNewStyleSet = (newEntry: { name: string, title: string, abstractText: string; }) => {
    const entry = {
      name: newEntry.name,
      title: newEntry.title,
      abstractText: newEntry.abstractText,
    }
    this.props.api.postProductStyleSetsDataById(this.props.productId, entry).then(styleSet => {
      this.setState({
        currentStyleSetId: styleSet.id,
      }, () => {
        const styledData = this.props.items.map(i => ({dataId: i.data, style: ""}));
        this.putStyleSetData(this.state.currentStyleSetId, styledData).then(ae => {
          this.loadAllStyles().then(() => {
            this.updateWmsStyle();
          });
        })
      });
    }, (err) => {
      // Add an error handling here...
    })
  }

  protected putStyleSetData = (id: string, styledData: any) => {
    return new Promise((resolve, reject) => {
      const cleanStyles = styledData.map(s => ({
        "data": s.dataId,
        "style": s.style ? s.style : null
      }))
      this.props.api.putProductStyleSetsDataById(this.props.productId, id, cleanStyles).then(availableStyleSets => {
        resolve(availableStyleSets);
      }, () => reject());
    })
  }

  updateBulkStyleHandlerNew = (styleToSet) => {
    function countStyles(selectedItems: {}, entry: StyleSet) {
      let count = 0;
      for (const key in selectedItems) {
        if (selectedItems.hasOwnProperty(key)) {
          const dataFound = entry.styles.find(s => s.dataId === key);
          if (dataFound) {
            count++;
            dataFound.style = styleToSet?.id;
            dataFound.styleTitle = styleToSet?.title;
          }
        }
      }
      return count;
    }

    this.loadAllStyles().then(() => {
      const availableStyleSets = [...this.state.availableStyleSets];
      const entry = availableStyleSets.find(c => c.id === this.state.currentStyleSetId);
      const selectedItems = {};
      if (entry) {
        const {items} = this.props;
        //save selected indices so the selection can be restored post-update
        for (let i = 0; i < items.length; i++) {
          const styledData = items[i];
          const isSelectedEntry = this.state.selectedIndices.indexOf(i) >= 0;
          if (isSelectedEntry) {
            selectedItems[styledData.data] = styledData
          }
        }

        const count = countStyles(selectedItems, entry);
        if (count > 0) {
          const cleanStyles = entry.styles.map(s => ({
            "data": s.dataId,
            "style": s.style ? s.style : null
          }))

          for (const key in selectedItems) {
            if (selectedItems.hasOwnProperty(key)) {
              selectedItems[key].style = styleToSet?.id;
              selectedItems[key].styleTitle = styleToSet?.title;
            }
          }

          this.props.api.putProductStyleSetsDataById(this.props.productId, this.state.currentStyleSetId,
              cleanStyles).then(result => {
            this.setState({
              availableStyleSets
            }, () => {
              this.loadAllStyles().then(() => {
                this.updateWmsStyle();
              })
            });
          })
        }
      }
    })
  }

  private setAsDefaultStyleSet = () => {
    this.setDefaultStyleSet(this.state.currentStyleSetId);
  }
  private deleteNewStyleSet = () => {
    const entry = this.state.availableStyleSets.find(c => c.name === "default");
    // Prevents deleting the default profile
    if (this.state.currentStyleSetId !== entry.id) {
      this.props.api.deleteProductStyleSetsDataById(this.props.productId, this.state.currentStyleSetId).then(status => {
        this.setState(prevState => ({
          currentStyleSetId: entry.id,
          defaultStyleSetId: prevState.defaultStyleSetId !== entry.id ? prevState.defaultStyleSetId : entry.id
        }), () => {
          this.loadAllStyles().then(() => {
            this.updateWmsStyle();
          });
        })
      })
    }
  }

  private updateWmsStyle = () => {
    const entry = this.state.availableStyleSets.find(c => c.id === this.state.currentStyleSetId);
    if (entry) {
      this.props.onSetWmsStyle(entry.name);
      setTimeout(() => {
        this.forceMapReload();
      }, 100)
    }
  }

  private generateStyleSetTitle = () => {
    return DefaultStyleSetTitlePrefix + Date.now();
  }

  render() {
    const {items, readOnly, intl} = this.props;
    const {dataToAdd, selectedIndices, isBulkRemovingEntries} = this.state;
    const validationResult: ValidationState = !dataToAdd ? null : "success";
    const hasSelectedEntries = this.state.selectedIndices.length > 0;
    const toggleVisibilityButton = (
        <Button bsSize="xs"
                disabled={!hasSelectedEntries}
                alt={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.toggleVisibilityDescription)}
                onClick={this.updateBulkVisibilityHandler}>
          <LcdIcon icon="eye"/>
          <FormattedMessage id="studio.products.product-content-list.toggle-visibility"
                            defaultMessage="Toggle visibility"/>
        </Button>
    );

    const currentSt = this.state.availableStyleSets.find(item => (item.id === this.state.currentStyleSetId));
    const itemsIndividual = this.getStyleSet(items, currentSt);
    const disableStyling = !this.props.productId || this.props.disableStyleEditing;
    const defaultStyleName = intl.formatMessage(PRODUCT_CONTENT_MESSAGES.defaultStyle);
    return (
        <div className="productContentList">
          {!readOnly && (
              <FormGroup validationState={validationResult}>
                <ImportedDataTypeahead value={dataToAdd}
                                       onChange={this.handleDataChange}
                                       validate={this.validateDataTypeHead}
                                       placeholder={this.props.addDataPlaceHolder || this.props.intl.formatMessage(
                                           PRODUCT_CONTENT_MESSAGES.dataPlaceholder)}
                />
                <FormControl.Feedback/>
              </FormGroup>)
          }
          <div>
            <FormGroup>
              <InputGroup>
                <FormControl componentClass={"select"}
                             style={{width: "100%"}} /* keep select fixed width so searchform looks the same on all pages */
                             name="a"
                             value={this.state.currentStyleSetId}
                             onChange={this.selectStyleSet}
                             disabled={disableStyling}
                >
                  {this.state.availableStyleSets.map(option => (
                      <option value={option.id} key={option.id} title={option.name}>
                        {(this.state.defaultStyleSetId !== option.id) ? option.title : option.title + " [ " +
                                                                                       defaultStyleName + " ]"}
                      </option>)
                  )
                  }
                </FormControl>
                <DropdownButton
                    componentClass={InputGroup.Button}
                    id="input-dropdown-addon"
                    title={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.actionTitle)}
                    style={{height: 32}}
                    disabled={disableStyling}
                >
                  <MenuItemSetStyleSetName initialTitle={this.generateStyleSetTitle()} onNameSet={this.addNewStyleSet}/>
                  <MenuItem onSelect={this.setAsDefaultStyleSet}>
                    <FormattedMessage id="studio.products.dropdownbutton.menuitem.set-as-default-set"
                                      defaultMessage="Set as default style set"/>
                  </MenuItem>
                  <MenuItem divider style={{backgroundColor: "black"}}/>
                  <MenuItem onSelect={this.deleteNewStyleSet}>
                    <FormattedMessage id="studio.products.dropdownbutton.menuitem.delete-style-set"
                                      defaultMessage="Delete style set"/>
                  </MenuItem>
                </DropdownButton>
              </InputGroup>

            </FormGroup>
            <ActionBar>
              <Button bsSize="xs" alt={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.selectAllItemsDescription)}
                      onClick={() => {
                        this.setState({selectedIndices: items.map((item, idx) => idx)});
                      }}>
                <LcdIcon icon="check"/><FormattedMessage
                  id="studio.products.product-content-list.select-all"
                  defaultMessage="Select all"/>
              </Button>
              <Button bsSize="xs" disabled={!hasSelectedEntries}
                      alt={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.clearSelectionDescription)} onClick={() => {
                this.setState({selectedIndices: []});
              }}>
                <LcdIcon icon="unchecked"/> <FormattedMessage
                  id="studio.products.product-content-list.clear-selection"
                  defaultMessage="Clear selection"/>
              </Button>
              <SetStyleButton bsSize="xs" disabled={!hasSelectedEntries || disableStyling}
                              onStyleSet={this.updateBulkStyleHandlerNew}/>
              <Button bsSize="xs" disabled={!hasSelectedEntries || isBulkRemovingEntries}
                      alt={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.removeSelectedItems)}
                      onClick={() => {
                        this.setState({isBulkRemovingEntries: true});
                        const removePromise = this.props.onBulkRemove(this.getSelectedStyledDatas(), this.props.items);
                        const onDoneRemoving = () => {
                          this.setState({isBulkRemovingEntries: false, selectedIndices: []});
                        };
                        removePromise.then(onDoneRemoving).catch(onDoneRemoving);
                        return removePromise;
                      }}>
                {!isBulkRemovingEntries && <LcdIcon icon="delete"/>}
                {isBulkRemovingEntries ?
                 <FormattedMessage id="studio.products.product-content-list.removing" defaultMessage="Removing..."/> :
                 <FormattedMessage id="studio.products.product-content-list.remove" defaultMessage="Remove"/>}
              </Button>
              {this.props.withVisibilityToggle && toggleVisibilityButton}
              <Button bsSize="xs" disabled={!hasSelectedEntries}
                      alt={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.moveSelectionTop)}
                      onClick={() => {
                        this.props.onReorder(this.state.selectedIndices, 0, this.props.items);
                        this.setState({
                          selectedIndices: Array.from(
                              {length: this.state.selectedIndices.length}, (_, i) => i)
                        });
                      }}>
                <LcdIcon icon="step-up"/>
                <FormattedMessage id="studio.products.product-content-list.move-selection-top-button"
                                  defaultMessage="Move to top"/>
              </Button>
              <Button bsSize="xs" disabled={!hasSelectedEntries}
                      alt={intl.formatMessage(PRODUCT_CONTENT_MESSAGES.moveSelectionBottom)}
                      onClick={() => {
                        this.props.onReorder(this.state.selectedIndices, this.props.items.length, this.props.items);
                        this.setState({
                          selectedIndices: Array.from(
                              {length: this.state.selectedIndices.length},
                              (_, i) => this.props.items.length - this.state.selectedIndices.length + i)
                        });
                      }}>
                <LcdIcon icon="step-down"/>
                <FormattedMessage id="studio.products.product-content-list.move-selection-bottom-button"
                                  defaultMessage="Move to bottom"/>
              </Button>
            </ActionBar>

            {this.renderErrors()}
            <this.ProductContentListTable data={itemsIndividual}
                                          intl={intl}
                                          isPaged={false}
                                          canMultiSelect={true}
                                          isAllDataLoaded={true}
                                          selectedRowIndices={selectedIndices}
                                          onRowSelect={(newSelectedEntries: StyledData[],
                                                        selectedRowIndices: number[]) => {
                                            this.setState({selectedIndices: selectedRowIndices});
                                            if (newSelectedEntries.length === 1) {
                                              this.props.selectItem(
                                                  newSelectedEntries[0] ? newSelectedEntries[0].id : null, true);
                                            } else {
                                              this.props.selectItem(null, true);
                                            }
                                          }}
                                          rowHeight={PRODUCT_DATA_LIST_ROW_HEIGHT}
                                          rowProps={(row: StyledData, rowIndex: number) => ({
                                            "data-index": rowIndex,
                                            draggable: true,
                                            onDragStart: this.onRowDragStart,
                                            onDragOver: this.onRowDragOver,
                                            onDragEnd: this.onRowDragEnd,
                                            onDrop: this.onRowDrop,
                                            style: {cursor: "grab"}
                                          })}
            />
          </div>
          <div className="numberReadout"><FormattedMessage id="studio.products.product-content-list.items-listed"
                                                           defaultMessage="Number of data items listed: {length}"
                                                           values={{length: itemsIndividual.length}}/></div>
        </div>
    );
  }

  private getStyleSet(items: StyledData[], currentSt: StyleSet) {
    let itemsIndividual = items;
    if (currentSt) {
      itemsIndividual = items.map(modItem => {
        const foundStyle = currentSt.styles.find(x => x.dataId === modItem.id);
        if (foundStyle) {
          return {...modItem, style: foundStyle.style ? foundStyle.style : null, styleTitle: foundStyle.styleTitle}
        } else {
          return {...modItem, style: null, styleTitle: ""}
        }
      });
    }
    return itemsIndividual;
  }
}

export const PRODUCT_DATA_LIST_ROW_HEIGHT = 38;

export const ProductContentListPresentation = WithApi(
    ReorderableList<StyledData, OwnProductContentListPropsWithApi>(injectIntl(ProductContentListComponent), null));

const mapStateToProps = (state, ownProps) => {
  const items = selectors.getProductContentsOfProduct(state, ownProps.productId);
  return {
    productId: ownProps.productId,
    items,
    withVisibilityToggle: ownProps.withVisibilityToggle,
    calculateDimensions: ownProps.calculateDimensions,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    productId: ownProps.productId,
    initialLoad: () => dispatch(actions.loadProductContents(ownProps.productId)),
    onReorder: (oldIndex, newIndex, items) => {
      const newContents = Array.isArray(oldIndex) ? moveItems<StyledData>(items, oldIndex, newIndex)
                                                  : moveItem<StyledData>(items, oldIndex, newIndex);
      dispatch(actions.setStyledData(ownProps.productId, newContents));
    },
    onAdd: (newItem) => {
      newItem.productId = ownProps.productId;
      dispatch(actions.createOrUpdateWithoutStyledData(ownProps.productId, [newItem]));
    },
    onChange: (changedItem) => {
      dispatch(actions.createOrUpdateWithoutStyledData(ownProps.productId, [changedItem]));
    },
    onBulkChange: (newStyledDatas) => {
      return dispatch(actions.createOrUpdateWithoutStyledData(ownProps.productId, newStyledDatas));
    },
    onBulkRemove: (removedStyledDatas, items) => {
      const newItems = items.filter((i) => !removedStyledDatas.includes(i));
      return dispatch(actions.setStyledData(ownProps.productId, newItems));
    },
  };
};

export const ProductContentList = connect(mapStateToProps, mapDispatchToProps)(ProductContentListPresentation);

/*
 * Some functions for calculating dimensions of this component, to be used in different places in ControlRoom
 */

export const productContentListDimensionsOnDetailPage = (parent: HTMLElement) => {
  const paddingLeft = parseFloat(getComputedStyle(parent).paddingLeft) || 0;
  const paddingRight = parseFloat(getComputedStyle(parent).paddingRight) || 0;
  const parentRect = parent.getBoundingClientRect();
  const parentTop = parentRect.top;
  const viewPortHeight = document.documentElement.clientHeight;
  const remainingHeightOnScreen = viewPortHeight - parentTop;
  const numberReadOutHeight = 25;
  const minHeight = 5 * PRODUCT_DATA_LIST_ROW_HEIGHT + CONTROLROOM_TABLE_HEADER_HEIGHT;
  return {
    width: parent.clientWidth - paddingLeft - paddingRight,
    height: Math.max(minHeight, remainingHeightOnScreen - numberReadOutHeight),
  };
};